import { useState } from 'react';
import type { ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import * as Dialog from '@radix-ui/react-dialog';
import { api } from '@/lib/api';
import { Button } from '@/ui/button';
import { MicroLabel } from '@/ui/micro-label';
import type { Profile } from '@/lib/contract';

/**
 * The account control in both shells used to sign out on a single click.
 *
 * It does not read as a destructive control -- in the vendor topbar it is an unlabelled circle that
 * looks like an avatar, and in the operator sidebar it is a card showing the account name -- so the
 * obvious reason to click it is to see who you are signed in as. Doing that ended the session, and
 * with MFA on, getting back in means the authenticator app again. One misread click is expensive.
 *
 * So the click now opens this: it answers the question the control appears to ask (who am I, in
 * which organisation) and makes signing out a second, deliberate step. The trigger is passed in
 * rather than rendered here because the two shells place it very differently and stay visually
 * distinct on purpose.
 */
export function SignOutDialog({
  realm,
  profile,
  trigger
}: {
  realm: 'vendor' | 'admin';
  profile: Profile | null | undefined;
  trigger: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const queryClient = useQueryClient();

  async function signOut() {
    setPending(true);
    await api('/logout', { method: 'POST', realm }).catch(() => {});
    // Clearing the cache before navigating matters: every cached query was fetched under the session
    // being ended, and the login screen must not be able to render a fragment of it.
    queryClient.clear();
    window.location.assign(realm === 'admin' ? '/admin/login' : '/v/login');
  }

  const context =
    realm === 'vendor'
      ? [profile?.organization_name, profile?.vendor_code].filter(Boolean).join(' · ')
      : (profile?.role ?? '');

  return (
    // A request already in flight ends in a navigation, so the dialog must not be dismissable
    // underneath it -- otherwise the shell flashes back for the moment before the page changes.
    <Dialog.Root open={open} onOpenChange={next => !pending && setOpen(next)}>
      <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/70" />
        <Dialog.Content className="fixed top-1/2 left-1/2 z-50 grid w-[min(400px,calc(100vw-32px))] -translate-x-1/2 -translate-y-1/2 gap-4 rounded border border-border bg-card p-6 shadow-2xl focus:outline-none">
          <div className="grid gap-1">
            <Dialog.Title className="text-[16px] font-medium text-foreground">Sign out?</Dialog.Title>
            <Dialog.Description className="text-[12px] leading-relaxed text-muted">
              You will need your password and a code from your authenticator to sign back in.
            </Dialog.Description>
          </div>

          <div className="grid gap-1 rounded border border-border bg-inset p-3">
            <span className="text-[13px] text-foreground">{profile?.name ?? 'Signed in'}</span>
            {profile?.email && <span className="font-mono text-[11px] text-muted">{profile.email}</span>}
            {context && <MicroLabel size="xs">{context}</MicroLabel>}
          </div>

          <div className="flex justify-end gap-2">
            <Dialog.Close asChild>
              <Button variant="secondary" size="sm" disabled={pending}>
                Cancel
              </Button>
            </Dialog.Close>
            <Button variant="primary" size="sm" onClick={signOut} disabled={pending}>
              {pending ? 'Signing out…' : 'Sign out'}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
